import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import {
  useBuilders,
  useCopyBuilder,
  useCreateBuilder,
  useDeleteBuilder,
  downloadBuilderExport,
  type CreateBuilderInput,
} from '../api/builders';
import { Breadcrumb } from '../components/ui/Breadcrumb';
import { Button } from '../components/ui/Button';
import { DataTable } from '../components/ui/DataTable';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { useToast } from '../hooks/useToast';
import { useAuthState } from '../auth/AuthProvider';
import page from './Page.module.css';
import styles from './BuildersPage.module.css';

type BuilderRow = {
  id: number;
  name: string;
  sage_customer_id?: string | null;
  entity_id?: number | null;
  entity_name?: string | null;
  community_count?: number;
  bidding_community_count?: number;
  awarded_community_count?: number;
  mto_count?: number;
  model_count?: number;
  models_missing_mto_count?: number;
  total_lines?: number;
};

function IconExport() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconTrash() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14zM10 11v6M14 11v6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconPillCommunities() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M3 21h18M5 21V7l8-4v18M19 21V11l-6-4M9 9v0M9 12v0M9 15v0M9 18v0" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconPillMtos() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconPillMissingData() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 9v4M12 17h.01" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconCopy() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function BuildersPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const auth = useAuthState();
  const { data, isLoading, error } = useBuilders();
  const create = useCreateBuilder();
  const copyBuilder = useCopyBuilder();
  const del = useDeleteBuilder();

  const [modalOpen, setModalOpen] = useState(false);
  const [name, setName] = useState('');
  const [sageCustomerId, setSageCustomerId] = useState('');
  const [notes, setNotes] = useState('');
  const [exportingId, setExportingId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<BuilderRow | null>(null);
  const [copyTarget, setCopyTarget] = useState<BuilderRow | null>(null);
  const [copyName, setCopyName] = useState('');

  const isAdmin = auth.status === 'authenticated' && auth.user.role === 'admin';

  function resetModal() {
    setName('');
    setSageCustomerId('');
    setNotes('');
  }

  function openModal() {
    resetModal();
    setModalOpen(true);
  }

  function openCopy(row: BuilderRow) {
    setCopyTarget(row);
    setCopyName(`Copy of ${row.name}`);
  }

  async function onCopy(e: React.FormEvent) {
    e.preventDefault();
    if (!copyTarget || !copyName.trim()) return;
    try {
      const created = await copyBuilder.mutateAsync({
        builderId: copyTarget.id,
        name: copyName.trim(),
      });
      toast('Builder copied');
      setCopyTarget(null);
      setCopyName('');
      navigate(`/builders/${created.id}`);
    } catch (ex) {
      toast(ex instanceof Error ? ex.message : 'Copy failed', true);
    }
  }

  async function onCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    const payload: CreateBuilderInput = {
      name: name.trim(),
      sage_customer_id: sageCustomerId.trim() || null,
      notes: notes.trim() || null,
    };
    try {
      await create.mutateAsync(payload);
      toast(`Created builder "${payload.name}"`);
      setModalOpen(false);
      resetModal();
    } catch (ex) {
      toast(ex instanceof Error ? ex.message : 'Create failed', true);
    }
  }

  function confirmDelete() {
    if (!deleteTarget) return;
    del.mutate(deleteTarget.id, {
      onSuccess: () => {
        toast('Builder deleted');
        setDeleteTarget(null);
      },
      onError: (ex) => toast(ex instanceof Error ? ex.message : 'Delete failed', true),
    });
  }

  const rows: BuilderRow[] = useMemo(() => (data ?? []) as BuilderRow[], [data]);

  const searchKeys = useMemo(
    () =>
      [
        'name' as const,
        (r: BuilderRow) => r.entity_name ?? '',
        (r: BuilderRow) => r.sage_customer_id ?? '',
        (r: BuilderRow) => String(r.community_count ?? 0),
        (r: BuilderRow) => String(r.bidding_community_count ?? 0),
        (r: BuilderRow) => String(r.awarded_community_count ?? 0),
        (r: BuilderRow) => String(r.mto_count ?? 0),
        (r: BuilderRow) => String(r.models_missing_mto_count ?? 0),
      ] as const,
    []
  );

  const columns = useMemo<ColumnDef<BuilderRow, unknown>[]>(
    () => [
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        meta: { align: 'left' as const },
        size: 100,
        cell: ({ row }) => {
          const b = row.original;
          return (
            <div className={styles.actions}>
              <button
                type="button"
                className={styles.iconBtn}
                title="Copy Builder"
                aria-label={`Copy ${b.name}`}
                onClick={(e) => {
                  e.stopPropagation();
                  openCopy(b);
                }}
              >
                <IconCopy />
              </button>
              <button
                type="button"
                className={styles.iconBtn}
                title="Export Excel"
                aria-label={`Export ${b.name} to Excel`}
                disabled={exportingId === b.id}
                onClick={() => {
                  setExportingId(b.id);
                  downloadBuilderExport(b.id)
                    .catch((ex) => toast(ex instanceof Error ? ex.message : 'Export failed', true))
                    .finally(() => setExportingId(null));
                }}
              >
                <IconExport />
              </button>
              {isAdmin ? (
                <button
                  type="button"
                  className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                  title="Delete Builder"
                  aria-label={`Delete ${b.name}`}
                  onClick={() => setDeleteTarget(b)}
                >
                  <IconTrash />
                </button>
              ) : null}
            </div>
          );
        },
      },
      {
        id: 'name',
        header: 'Builder',
        accessorKey: 'name',
        cell: ({ row }) => (
          <div className={styles.nameCell}>
            <Link className={styles.nameLink} to={`/builders/${row.original.id}`}>
              {row.original.name}
            </Link>
          </div>
        ),
      },
      {
        id: 'entity',
        header: 'Entity',
        accessorFn: (r) => r.entity_name?.trim() || '',
        cell: ({ row }) => {
          const name = row.original.entity_name?.trim();
          return name ? name : <span className={page.muted}>—</span>;
        },
      },
      {
        id: 'sage_id',
        header: 'Sage ID',
        accessorFn: (r) => r.sage_customer_id?.trim() || '',
        cell: ({ row }) => {
          const sage = row.original.sage_customer_id?.trim();
          return sage ? sage : <span className={page.muted}>—</span>;
        },
      },
      {
        id: 'communities',
        header: 'Communities',
        accessorKey: 'community_count',
        meta: { align: 'right' as const, nowrap: true },
        cell: ({ row }) => {
          const c = Number(row.original.community_count ?? 0);
          const bidding = Number(row.original.bidding_community_count ?? 0);
          const awarded = Number(row.original.awarded_community_count ?? 0);
          return (
            <span
              className={`${styles.pill} ${styles.pillCommunities}`}
              title={`${c} communities · ${bidding} bidding · ${awarded} awarded`}
            >
              <IconPillCommunities />
              {c}
              {c > 0 ? (
                <span className={styles.pillSplit}>
                  {bidding} Bidding · {awarded} Awarded
                </span>
              ) : null}
            </span>
          );
        },
      },
      {
        id: 'mtos',
        header: 'MTOs',
        accessorKey: 'mto_count',
        meta: { align: 'right' as const, nowrap: true },
        cell: ({ row }) => {
          const m = Number(row.original.mto_count ?? 0);
          return (
            <span className={`${styles.pill} ${styles.pillMtos}`} title={`${m} MTOs`}>
              <IconPillMtos />
              {m}
            </span>
          );
        },
      },
      {
        id: 'missing_data',
        header: 'Missing Data',
        accessorKey: 'models_missing_mto_count',
        meta: { align: 'right' as const, nowrap: true },
        cell: ({ row }) => {
          const modelCount = Number(row.original.model_count ?? 0);
          const missing = Number(row.original.models_missing_mto_count ?? 0);
          if (modelCount === 0) return <span className={page.muted}>—</span>;
          return (
            <span
              className={`${styles.pill} ${missing > 0 ? styles.pillMissingData : styles.pillMissingDataOk}`}
              title={
                missing > 0
                  ? `${missing} of ${modelCount} catalog models missing MTO data`
                  : `All ${modelCount} catalog models have MTO data`
              }
            >
              <IconPillMissingData />
              {missing}
            </span>
          );
        },
      },
    ],
    [exportingId, isAdmin, toast]
  );

  return (
    <div>
      <Breadcrumb items={[{ label: 'Builders' }]} />

      <div className={styles.toolbar}>
        <Button type="button" variant="success" onClick={openModal}>
          Add New Builder
        </Button>
      </div>

      <div className={page.card}>
        <h2 className={page.title} style={{ marginBottom: 16 }}>
          Builders
        </h2>
        {isLoading ? <p className={page.muted}>Loading…</p> : null}
        {error ? <p style={{ color: 'var(--color-danger)' }}>{String(error)}</p> : null}
        {!isLoading && !error && rows.length === 0 ? (
          <p className={page.muted}>No builders yet. Use &quot;Add New Builder&quot; to create one.</p>
        ) : null}
        {!isLoading && rows.length > 0 ? (
          <DataTable data={rows} columns={columns} searchKeys={searchKeys} />
        ) : null}
      </div>

      <Modal
        title="Add New Builder"
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        size="wide"
        showClose={false}
      >
        <form onSubmit={onCreate} className={styles.modalForm}>
          <Input label="Builder Name" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
          <Input
            label="Sage Customer ID"
            value={sageCustomerId}
            onChange={(e) => setSageCustomerId(e.target.value)}
            placeholder="Optional"
          />
          <label className={page.muted} style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
            Notes
          </label>
          <textarea
            className={styles.textarea}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional"
            rows={4}
          />
          <div className={styles.modalActions}>
            <Button type="button" variant="ghost" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="success" disabled={create.isPending || !name.trim()}>
              {create.isPending ? 'Creating…' : 'Create Builder'}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        title="Copy Builder"
        open={!!copyTarget}
        onClose={() => {
          setCopyTarget(null);
          setCopyName('');
        }}
        size="md"
        showClose={false}
      >
        <form onSubmit={onCopy} className={styles.modalForm}>
          <p className={page.muted} style={{ margin: '0 0 8px', fontSize: 13 }}>
            Source:{' '}
            <span style={{ fontWeight: 600, color: 'var(--color-navy, #0f172a)' }}>
              {copyTarget?.name}
            </span>
          </p>
          <Input
            label="New Name"
            value={copyName}
            onChange={(e) => setCopyName(e.target.value)}
            required
            autoFocus
          />
          <p className={page.muted} style={{ margin: 0, fontSize: 13 }}>
            Copies catalog models and all active communities (settings, MTOs, labor, suppliers). Bid
            revisions are not copied. Sage Customer ID is left blank on the new builder.
          </p>
          <div className={styles.modalActions}>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setCopyTarget(null);
                setCopyName('');
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="success"
              disabled={copyBuilder.isPending || !copyName.trim()}
            >
              {copyBuilder.isPending ? 'Copying…' : 'Copy Builder'}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        title="Delete Builder?"
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        showClose={false}
      >
        {deleteTarget ? (
          <>
            <div className={styles.deleteBody}>
              <p className={styles.deleteLead}>Delete &quot;{deleteTarget.name}&quot;?</p>
              <p className={styles.deleteWarn}>
                This removes the builder and all related communities, MTOs, lines, and bid data. This cannot be undone.
              </p>
            </div>
            <div className={styles.modalActions}>
              <Button type="button" variant="ghost" onClick={() => setDeleteTarget(null)} disabled={del.isPending}>
                Cancel
              </Button>
              <Button type="button" variant="danger" onClick={confirmDelete} disabled={del.isPending}>
                {del.isPending ? 'Deleting…' : 'Delete Builder'}
              </Button>
            </div>
          </>
        ) : null}
      </Modal>
    </div>
  );
}
